'use strict';

// do not require the style file here - it generates the css
// twice - once in the foot.css and once in the [entry].css
// added into the chunks/foot in the webpack config
//require('./SimpleModal-style.scss');
const $ = require('dibs-jquery-shim');
const { BaseView, View } = require('dibs-backbone');
const { simpleModalTemplate: template } = require('./SimpleModal-template');
const { transitionElement } = require('../../global/utils/transitionHelpers');
const mergeClassNames = require('../../global/utils/mergeClassNames');
const animationFrame = require('../../global/utils/requestAnimationFrame');
const CloseIcon = require('dibs-vg/dist/templateString/close');
let $body;

/**
 * Simple modal that handle hide/show, close on escape, etc. Consumers can pass in an HTML string, DOMNode, JQuery
 * object or Backbone View for the content. Consumers are expected to construct the Backbone View first and then
 * pass the created instance into the modal constructor. They are also expected to render the content view and then
 * render the modal afterwards.
 *
 * @param  {string|Backbone.View|JQuery|DOMNode} options.content The inner content of the modal.
 * @param  {object} [options.classNames] Set of class names to add to the modal for style customization. Options are
 *                                       `modal` for the outer wrapper, `overlay` for the background overlay, `close`
 *                                        for the close button, contentWrapper for the wrapper around the passed in
 *                                        content and `content` for the content itself.
 * @param  {boolean} [options.manualHideCallback] Callback to fire whenever the modal is hidden manually (close button, escape, background click).
 * @param  {boolean} [options.closeButton=true] Whether or not to automatically add a close button.
 * @param  {boolean} [options.closeOnEscape=true] Whether or not to close the modal on pressing escape.
 * @param  {boolean} [options.closeOnBackgroundClick=true] Whether or not to close the modal on clicking the background overlay.
 * @return {object}
 */
class SimpleModal extends BaseView {
    constructor(options = {}) {
        super(options);

        $body = $('body');
        const {
            manualHideCallback,
            closeOnEscape,
            closeButton,
            closeOnBackgroundClick,
            content,
            classNames,
        } = options;

        if (typeof content === 'undefined') {
            throw new Error('SimpleModal requires a content property.');
        }

        // The main content to be displayed in the modal.
        this._content = content;
        this._classNames = mergeClassNames(
            {
                modal: '',
                overlay: '',
                close: '',
                contentWrapper: '',
                content: '',
            },
            this.classNames(),
            classNames
        );
        this._manualHideCallback = manualHideCallback;
        this._closeOnEscape = typeof closeOnEscape !== 'undefined' ? closeOnEscape : true;
        this._closeButton = typeof closeButton !== 'undefined' ? closeButton : true;
        this._closeOnBackgroundClick =
            typeof closeOnBackgroundClick !== 'undefined' ? closeOnBackgroundClick : true;
        this._canManualHide = true;

        if (this._closeOnEscape) {
            this._handleKeydown = e => {
                if (e.which === 27) {
                    this._handleManualHide(e);
                }
            };
        }
    }

    events() {
        return {
            'click .simple-modal-close': '_handleCloseClick',
            'touchend .simple-modal-close': '_handleCloseClick',
            'click .simple-modal-overlay': '_handleBackgroundClick',
        };
    }

    /**
     * Return a JQuery selector indicating where to put the close button. If not implemented the close button will be
     * prepended to the content wrapper.
     *
     * @abstract
     * @return {string} JQuery selector.
     */
    closeButtonLocation() {}

    /**
     * Additional content to insert before the main content.
     *
     * @abstract
     * @return {object|string} DOM Node, JQuery object or HTML string.
     */
    preContent() {}

    /**
     * Additional content to insert after the main content.
     *
     * @abstract
     * @return {object|string} DOM Node, JQuery object or HTML string.
     */
    postContent() {}

    /**
     * Action to take immediately after rendering
     *
     * @abstract
     */
    afterRender() {}

    /**
     * Class names to set on the elements of the modal in addition to any class names that are passed into the
     * constructor.
     *
     * @abstract
     * @return {object} Class names objects.
     */
    classNames() {}

    /**
     * Render the modal with the content inside. If the content is a Backbone View, it should already be rendered
     * before the modal is rendered.
     *
     * @return {object}
     */
    render() {
        const preContent = this.preContent();
        const postContent = this.postContent();
        const closeButtonLocation = this.closeButtonLocation();
        let content = (this.content = this._content);

        if (content && content instanceof View) {
            content = content.el;
        }

        this.setElement(
            template({
                classNames: this._classNames,
                closeIcon: CloseIcon,
            }).trim()
        );
        this.$closeButton = this.$('.simple-modal-close');
        this.$contentWrapper = this.$('.simple-modal-content-wrapper');
        this.$content = $(content);

        if (this._classNames.content) {
            this.$content.addClass(this._classNames.content);
        }

        if (preContent) {
            this.$contentWrapper.append(preContent);
        }

        this.$contentWrapper.append(this.$content);

        if (postContent) {
            this.$contentWrapper.append(postContent);
        }

        if (closeButtonLocation) {
            this.$closeButton.appendTo(this.$(closeButtonLocation));
        }

        if (!this._closeButton) {
            this._hideCloseButton();
        }

        $body.append(this.el);
        this.afterRender();

        return this;
    }

    show(callback) {
        transitionElement(this.$el, {
            startCallback: () => {
                this.$el.addClass('open visible');
                this.$el.css('opacity');

                let classes = 'prevent-interaction lock-position no-scroll';

                if (this.$el.hasClass('is-full-page')) {
                    classes += ' prevent-scroll-to-top';
                }

                $body.addClass(classes);
            },

            endCallback: () => {
                if (this._closeOnEscape) {
                    $(document).on('keydown', this._handleKeydown);
                }

                $body.removeClass('prevent-interaction');

                if (typeof callback === 'function') {
                    callback();
                }

                this.trigger('show');
            },
        });
    }

    hide(callback) {
        transitionElement(this.$el, {
            startCallback: () => {
                if (this._closeOnEscape) {
                    this._removeKeydownHandler();
                }

                this.$el.removeClass('open');
                $body.addClass('prevent-interaction');
            },

            endCallback: () => {
                let classes = 'prevent-interaction lock-position no-scroll';

                if (this.$el.hasClass('is-full-page')) {
                    classes += ' prevent-scroll-to-top';
                }

                $body.removeClass(classes);
                this.$el.removeClass('visible');

                if (typeof callback === 'function') {
                    callback();
                }

                this.trigger('hide');

                animationFrame(() => {
                    this.$el.removeClass('visible');
                });
            },
        });
    }

    /**
     * Temporarily prevent the user from closing the modal through either the close button, hitting escape or clicking
     * on the background overlay. This can be reversed with `enableManualHide`.
     */
    disableManualHide() {
        this._canManualHide = false;
        this._hideCloseButton();
    }

    /**
     * Allow the user to close the modal after previously disabling ths ability.
     */
    enableManualHide() {
        this._canManualHide = true;

        if (this._closeButton) {
            this._showCloseButton();
        }
    }

    _handleCloseClick(e) {
        if (this._closeButton) {
            this._handleManualHide(e);
        }
    }

    _handleManualHide(e) {
        e.preventDefault();

        if (this._canManualHide) {
            this.hide(this._manualHideCallback);
        }
    }

    _hideCloseButton() {
        this.$closeButton.hide();
    }

    _showCloseButton() {
        this.$closeButton.show();
    }

    _removeKeydownHandler() {
        $(document).off('keydown', this._handleKeydown);
    }

    _handleBackgroundClick(e) {
        if (this._closeOnBackgroundClick) {
            this._handleManualHide(e);
        }
    }
}

module.exports = SimpleModal;



// WEBPACK FOOTER //
// ./assets/js/components/SimpleModal/SimpleModal-view.es.js